/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';
import { cssMap, jsx } from '@atlaskit/css';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import messages from '../../../common/utils/messages';
import {
	NAVIGATION_CHANNEL,
	OPERATIONAL_EVENT_TYPE,
	SWITCHER_PARTIAL_RESULTS,
} from '../../../common/utils/analytics';
import { FormattedMessage } from 'react-intl-next';
import { type AnalyticsEventPayload, useAnalyticsEvents } from '@atlaskit/analytics-next';
import WarningIcon from '@atlaskit/icon/core/warning';
import { useIsSwitcherTeam25TestCohort } from '../experiment-provider';

const style = cssMap({
	errorIconStyles: {
		marginBottom: token('space.100'),
	},

	errorStyles: {
		display: 'flex',
		alignItems: 'center',
		flexDirection: 'column',
		textAlign: 'center',
		marginTop: token('space.100'),
		marginRight: token('space.050'),
		marginBottom: token('space.200'),
		marginLeft: token('space.050'),
	},

	textSpacingStyles: {
		marginBottom: token('space.025'),
	},
});

const ACTION = 'rendered';

export const PartialResultError = ({ emptyResult }: { emptyResult: boolean }) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isVisualRefreshEnabled = useIsSwitcherTeam25TestCohort();

	const fireOperationalEvent = React.useCallback(
		(payload: AnalyticsEventPayload) => {
			if (createAnalyticsEvent) {
				createAnalyticsEvent({
					eventType: OPERATIONAL_EVENT_TYPE,
					actionSubject: SWITCHER_PARTIAL_RESULTS,
					action: ACTION,
					...payload,
				}).fire(NAVIGATION_CHANNEL);
			}
		},
		[createAnalyticsEvent],
	);

	React.useEffect(() => {
		fireOperationalEvent({
			attributes: {
				emptyResult,
			},
		});
	}, [emptyResult, fireOperationalEvent]);

	return (
		<Box xcss={style.errorStyles} testId="switcher-partial-error">
			<Box xcss={style.errorIconStyles}>
				<WarningIcon label="" aria-hidden="true" />
			</Box>
			<Box xcss={isVisualRefreshEnabled ? style.textSpacingStyles : undefined}>
				<Text as="p">
					<FormattedMessage
						{...(emptyResult ? messages.errorEmptyPartialResults : messages.errorPartialResults)}
					/>
				</Text>
			</Box>
			<Text as="p">
				<FormattedMessage {...messages.errorPartialResultsRefresh} />
			</Text>
		</Box>
	);
};
